import React, { useState, useEffect, useCallback } from 'react';
import { AlertOptions, AlertResult } from '../interfaces';
import '../css/alert.css';


// Alert Context
const AlertContext = React.createContext<{
  showAlert: (options: AlertOptions) => Promise<AlertResult>;
} | null>(null);

// Hook para usar las alertas
export const useAlert = () => {
  const context = React.useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within AlertProvider');
  }
  return context;
};

// Componente de la alerta individual
const AlertModal: React.FC<{
  options: AlertOptions;
  onClose: (result: AlertResult) => void;
  isVisible: boolean;
}> = ({ options, onClose, isVisible }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [timeLeft, setTimeLeft] = useState(options.timer || 0);

  const getIconContent = () => {
    switch (options.icon) {
      case 'success':
        return (
          <div className="alert-icon alert-icon-success">
            <div className="success-checkmark">
              <svg className="checkmark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
                <path className="checkmark-check" fill="none" d="m14.1 27.2l7.1 7.2 16.7-16.8"/>
              </svg>
            </div>
          </div>
        );
      case 'error':
        return (
          <div className="alert-icon alert-icon-error">
            <div className="alert-icon-content">✕</div>
          </div>
        );
      case 'warning':
        return (
          <div className="alert-icon alert-icon-warning">
            <div className="alert-icon-content">!</div>
          </div>
        );
      case 'info':
        return (
          <div className="alert-icon alert-icon-info">
            <div className="alert-icon-content">i</div>
          </div>
        );
      case 'question':
        return (
          <div className="alert-icon alert-icon-question">
            <div className="alert-icon-content">?</div>
          </div>
        );
      case 'spinner':
        return (
          <div className="alert-icon alert-icon-spinner">
            <div className="spinner"></div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleConfirm = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => {
      onClose({ isConfirmed: true, isDenied: false, isDismissed: false });
    }, 150);
  }, [onClose]);

  const handleCancel = useCallback(() => {
    setIsAnimating(true);
    setTimeout(() => {
      onClose({ isConfirmed: false, isDenied: true, isDismissed: false });
    }, 150);
  }, [onClose]);

  const handleDismiss = useCallback(() => {
    if (options.allowOutsideClick === false) return;
    setIsAnimating(true);
    setTimeout(() => {
      onClose({ isConfirmed: false, isDenied: false, isDismissed: true });
    }, 150);
  }, [onClose, options.allowOutsideClick]);

  // Auto close timer
  useEffect(() => {
    if (!options.timer || !options.autoClose) return;

    setTimeLeft(options.timer);

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 100) {
          clearInterval(interval);
          handleDismiss();
          return 0;
        }
        return prev - 100;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [options.timer, options.autoClose, handleDismiss]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && options.allowEscapeKey !== false) {
        handleDismiss();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, handleDismiss, options.allowEscapeKey]);

  if (!isVisible) return null;

  const progressPercentage = options.timer && options.autoClose 
    ? ((options.timer - timeLeft) / options.timer) * 100 
    : 0;

  return (
    <div 
      className={`alert-overlay ${isAnimating ? 'closing' : ''}`}
      onClick={handleDismiss}
    >
      <div 
        className={`alert-modal ${isAnimating ? 'closing' : ''} ${options.customClass || ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {options.icon && getIconContent()}
        
        {options.title && (
          <div className="alert-title">{options.title}</div>
        )}
        
        {options.text && (
          <div className="alert-text">{options.text}</div>
        )}
        
        <div className="alert-buttons">
          {options.showCancelButton && (
            <button 
              className="alert-button alert-button-cancel"
              onClick={handleCancel}
              style={options.cancelButtonColor ? {
                background: options.cancelButtonColor,
                borderColor: options.cancelButtonColor
              } : {}}
            >
              {options.cancelButtonText || 'Cancelar'}
            </button>
          )}
          
          {options.showConfirmButton !== false && (
            <button 
              className="alert-button alert-button-confirm"
              onClick={handleConfirm}
              style={options.confirmButtonColor ? {
                background: options.confirmButtonColor,
                borderColor: options.confirmButtonColor
              } : {}}
            >
              {options.confirmButtonText || 'OK'}
            </button>
          )}
        </div>

        {options.timer && options.autoClose && (
          <div 
            className="alert-timer-progress"
            style={{ width: `${progressPercentage}%` }}
          />
        )}
      </div>
    </div>
  );
};

// Provider component
export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<Array<{
    id: string;
    options: AlertOptions;
    resolve: (result: AlertResult) => void;
  }>>([]);

  const showAlert = useCallback((options: AlertOptions): Promise<AlertResult> => {
    return new Promise((resolve) => {
      const id = Math.random().toString(36).substr(2, 9);
      setAlerts(prev => [...prev, { id, options, resolve }]);
    });
  }, []);

  const closeAlert = useCallback((id: string, result: AlertResult) => {
    setAlerts(prev => {
      const alert = prev.find(a => a.id === id);
      if (alert) {
        alert.resolve(result);
      }
      return prev.filter(a => a.id !== id);
    });
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      {alerts.map(alert => (
        <AlertModal
          key={alert.id}
          options={alert.options}
          onClose={(result) => closeAlert(alert.id, result)}
          isVisible={true}
        />
      ))}
    </AlertContext.Provider>
  );
};

// Funciones de conveniencia
export const Alert = {
  success: (title: string, text?: string, timer?: number): AlertOptions => ({
    title,
    text,
    icon: 'success',
    timer,
    autoClose: !!timer
  }),
  
  error: (title: string, text?: string): AlertOptions => ({
    title,
    text,
    icon: 'error'
  }),
  
  warning: (title: string, text?: string): AlertOptions => ({
    title,
    text,
    icon: 'warning',
    showCancelButton: true
  }),
  
  info: (title: string, text?: string): AlertOptions => ({
    title,
    text,
    icon: 'info'
  }),
  
  question: (title: string, text?: string): AlertOptions => ({
    title,
    text,
    icon: 'question',
    showCancelButton: true
  }),

  spinner: (title: string, text?: string, timer?: number): AlertOptions => ({
    title,
    text,
    icon: 'spinner',
    timer,
    autoClose: !!timer,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false
  })
};

export default AlertProvider;